// lib/aktionen/kontext.ts
// Kontext-Builder für die Aktions-Engine (Etappe 4): dünner, asynchroner Wrapper
// (Konvention: nicht unit-getestet) um die vorhandenen Ladefunktionen. Baut aus
// einer Ticket-Basis den vollen AktionKontext inkl. Stammdaten für die Mail-Module.
import { supabase } from '../supabase/client';
import { ladeAuftragsDaten } from '../tickets/ladeAuftragsDaten';
import type { AuftragsDaten } from '../tickets/auftragsMail';
import type { AktionKontext } from './types';
import type { PlatzhalterWerte } from './platzhalter';

type Stammdaten = NonNullable<AktionKontext['stammdaten']>;
type Mieter = Stammdaten['mieter'];
type Dienstleister = Stammdaten['dienstleister'];
type Vorlage = Stammdaten['vorlagen'][number];
type Objekt = AuftragsDaten['objekt'];
type Absender = { name: string | null; email: string | null } | null;

/**
 * Rein & testbar: baut die {{key}}-Werte für den Platzhalter-Resolver aus den
 * geladenen Stammdaten. Fehlende Werte werden zu '' (nicht null/undefined),
 * damit ersetzePlatzhalter() keine Lücken lässt.
 */
export function bauePlatzhalterWerte(mieter: Mieter, objekt: Objekt, absender: Absender, heute: string): PlatzhalterWerte {
  return {
    'mieter.anrede': mieter?.anrede ?? '',
    'mieter.vorname': mieter?.vorname ?? '',
    'mieter.nachname': mieter?.nachname ?? '',
    'mieter.email': mieter?.email ?? '',
    'mieter.telefon': mieter?.telefon ?? '',
    'objekt.bezeichnung': objekt?.bezeichnung ?? '',
    'objekt.strasse': objekt?.strasse ?? '',
    'objekt.hausnummer': objekt?.hausnummer ?? '',
    'objekt.plz': objekt?.plz ?? '',
    'objekt.ort': objekt?.ort ?? '',
    'datum.heute': heute,
    'absender.name': absender?.name ?? '',
    'absender.email': absender?.email ?? '',
  };
}

/**
 * Baut den vollen AktionKontext für ein (frisch erstelltes oder bestehendes) Ticket.
 * Best effort: jeder Ladeblock ist einzeln try/catch-abgesichert -> Fehler in einem
 * Teil (z.B. fehlende Migration) darf die anderen Teile nicht verhindern.
 * `regelName` ist für künftige Module reserviert (Vorlagen-Zuordnung in Etappe 4b/Task 4a).
 */
export async function baueAktionsKontext(
  ticketBasis: AktionKontext['ticket'],
  regelName?: string | null,
): Promise<AktionKontext> {
  void regelName;

  let auftrag: AuftragsDaten | null = null;
  let anhaenge: Stammdaten['anhaenge'] = [];
  let absenderEmailAusMail: string | null = null;
  try {
    const geladen = await ladeAuftragsDaten(ticketBasis.id);
    auftrag = geladen.daten;
    anhaenge = geladen.anhaenge.map((a) => ({ name: a.name, path: a.path, size: a.size }));
    absenderEmailAusMail = geladen.daten.meldender?.email ?? null;
  } catch (e) {
    console.warn('baueAktionsKontext: ladeAuftragsDaten fehlgeschlagen', e);
  }

  let mieter: Mieter = null;
  if (ticketBasis.mieter_id) {
    try {
      const { data } = await supabase
        .from('mieter')
        .select('vorname, nachname, anrede, email, telefon, mobil')
        .eq('id', ticketBasis.mieter_id)
        .maybeSingle();
      if (data) {
        mieter = {
          name: [data.vorname, data.nachname].filter(Boolean).join(' ') || null,
          anrede: data.anrede ?? null,
          vorname: data.vorname ?? null,
          nachname: data.nachname ?? null,
          email: data.email ?? null,
          telefon: data.telefon || data.mobil || null,
        };
      }
    } catch (e) {
      console.warn('baueAktionsKontext: Mieter laden fehlgeschlagen', e);
    }
  }

  let dienstleister: Dienstleister = null;
  if (ticketBasis.dienstleister_vorschlag_id) {
    try {
      const { data } = await supabase
        .from('dienstleister')
        .select('id, name, email, beauftragung_modus')
        .eq('id', ticketBasis.dienstleister_vorschlag_id)
        .maybeSingle();
      if (data) {
        dienstleister = { id: data.id, name: data.name ?? null, email: data.email ?? null, beauftragung_modus: data.beauftragung_modus ?? null };
      }
    } catch (e) {
      console.warn('baueAktionsKontext: Dienstleister laden fehlgeschlagen', e);
    }
  }

  let teamName: string | null = null;
  if (ticketBasis.bearbeiter_vorschlag_typ === 'team' && ticketBasis.bearbeiter_vorschlag_id) {
    try {
      const { data } = await supabase.from('teams').select('name').eq('id', ticketBasis.bearbeiter_vorschlag_id).maybeSingle();
      teamName = data?.name ?? null;
    } catch (e) {
      console.warn('baueAktionsKontext: Team laden fehlgeschlagen', e);
    }
  }

  let vorlagen: Vorlage[] = [];
  try {
    const { data, error } = await supabase
      .from('email_vorlagen')
      .select('id, name, ki_trigger, betreff_template, html_template')
      .eq('aktiv', true);
    if (error) throw error;
    vorlagen = (data || []) as Vorlage[];
  } catch (e) {
    // ki_trigger existiert live evtl. noch nicht (Migration kommt erst beim Rollout) -> Fallback ohne die Spalte.
    try {
      const { data } = await supabase.from('email_vorlagen').select('id, name, betreff_template, html_template').eq('aktiv', true);
      vorlagen = (data || []).map((v: any) => ({ ...v, ki_trigger: null }));
    } catch (e2) {
      console.warn('baueAktionsKontext: Vorlagen laden fehlgeschlagen', e2);
    }
  }

  let absender: Absender = null;
  try {
    const { data } = await supabase.from('email_konten').select('email_adresse, bezeichnung').eq('aktiv', true).limit(1).maybeSingle();
    if (data) absender = { name: data.bezeichnung ?? null, email: data.email_adresse ?? null };
  } catch (e) {
    console.warn('baueAktionsKontext: E-Mail-Konto laden fehlgeschlagen', e);
  }

  const heute = new Date().toLocaleDateString('de-DE');
  const platzhalter = bauePlatzhalterWerte(mieter, auftrag?.objekt ?? null, absender, heute);
  const absenderEmail = mieter?.email ?? absenderEmailAusMail ?? null;

  return {
    ticket: ticketBasis,
    stammdaten: { absenderEmail, mieter, dienstleister, teamName, auftrag, anhaenge, vorlagen, platzhalter },
  };
}
